import { AppData } from '@lightningjs/sdk';
import { AbstractDeviceIntegration } from 'config/platforms/AbstractDeviceIntegration';
import StorageService from 'services/StorageService';
import ComscoreAnalytics from 'services/analytics/Comscore';
import MParticleAnalytics from 'services/analytics/MParticle';
import NielsenAnalytics from 'services/analytics/Nielsen';
import { OneTrustPurposeStatus } from 'types/oneTrust';

export type Privacy = {
  privacyViewed: boolean; // viewed privacy setting
  optOut: boolean; // opt out to not selling
  oneTrustMigrated?: boolean; // OneTrust has reported consent on this device
  oneTrustCarriedOptOut?: boolean; // legacy opt out, held until the user closes the OneTrust preference center
};

export type OneTrustConsent = {
  targeting: OneTrustPurposeStatus;
  usPrivacy: string | null; // OneTrust's IAB US Privacy string, unvalidated
  gppString: string | null; // OneTrust's IAB GPP string, unvalidated
  gppSid: string | null; // OneTrust's applicable GPP section ids, underscore separated
};

export type IabGpp = {
  gpp: string;
  gppSid: string; // comma separated
};

export type PrivacyConsent = 'opt-in' | 'opt-out';

type Ccpa = `1${'-' | 'Y' | 'N'}${'-' | 'Y' | 'N'}${'-' | 'Y' | 'N'}`;

const CCPA_PATTERN = /^1[-YN]{3}$/;
const GPP_SID_PATTERN = /^-?\d+(_-?\d+)*$/;

const isCcpa = (value: string): value is Ccpa => CCPA_PATTERN.test(value);

type ConstructorArgs = {
  deviceIntegration: AbstractDeviceIntegration;
  storageService: StorageService;
};

type InitPrivacyStatusArgs = {
  comscore: ComscoreAnalytics;
  mParticle: MParticleAnalytics;
  nielsen: NielsenAnalytics;
};

enum CcpaStatus {
  OFF = '1YNN', // has not viewed (privacyViewed false)
  OFF_VIEWED = '1YNN', // navigating to the screen with the toggle does not provide sufficient notice per our legal counsel, so the string is not any different from OFF
  ON = '1YYN', // Viewed and Not Sell Data (privacyViewed true, optOut true)
}

enum rdpStatus {
  OFF = 0, // has not viewed (privacyViewed false)
  OFF_VIEWED = 0, // Viewed and Sell Data (privacyViewed true, optOut false/null)
  ON = 1, // Viewed and Not Sell Data (privacyViewed true, optOut true)
}
const PRIVACY_DEFAULT = {
  privacyViewed: true, // defaults to true
  optOut: false,
};

export default class PrivacyService {
  private readonly deviceIntegration: AbstractDeviceIntegration;
  private readonly storageService;
  private comscore: ComscoreAnalytics | null = null;
  private mParticle: MParticleAnalytics | null = null;
  private nielsen: NielsenAnalytics | null = null;

  private appPrivacySettings: Privacy | undefined;
  // Held while OneTrust is running, it replaces the in-app toggle
  private oneTrustConsent: OneTrustConsent | null = null;

  constructor({ deviceIntegration, storageService }: ConstructorArgs) {
    this.deviceIntegration = deviceIntegration;
    this.storageService = storageService;

    this.deviceIntegration.addPrivacyChangeListener(
      this.updateAnalyticsPrivacyStatus.bind(this),
    );
  }

  private lazyInit() {
    if (this.appPrivacySettings) return;

    this.setDefault();
  }

  private updateAppPrivacySettings() {
    if (this.appPrivacySettings !== undefined) {
      this.storageService.privacy.set(this.appPrivacySettings);
    }
  }

  private getOneTrustOptOut(): boolean {
    return (
      !!this.appPrivacySettings?.oneTrustCarriedOptOut ||
      this.oneTrustConsent?.targeting === 0
    );
  }

  private updateAnalyticsPrivacyStatus() {
    const privacyConsent: PrivacyConsent = this.getDoNotSell()
      ? 'opt-out'
      : 'opt-in';

    AppData?.log.debug(
      `[Privacy][Privacy Service] updateAnalyticsPrivacyStatus "consent":${privacyConsent}`,
    );

    this.comscore?.updatePrivacyStatus(privacyConsent);
    this.mParticle?.updatePrivacyStatus(privacyConsent);
    this.nielsen?.updatePrivacyStatus(privacyConsent);
  }

  setDefault() {
    const appPrivacySettings = this.storageService.privacy.get();
    this.appPrivacySettings = appPrivacySettings ?? { ...PRIVACY_DEFAULT };
  }

  /** Updates analytic's privacy status on app start */
  initPrivacyStatus({ comscore, mParticle, nielsen }: InitPrivacyStatusArgs) {
    this.lazyInit();

    this.comscore = comscore;
    this.mParticle = mParticle;
    this.nielsen = nielsen;
    this.updateAnalyticsPrivacyStatus();
  }

  setDoNotSell(optOut: boolean) {
    this.lazyInit();

    this.appPrivacySettings!.optOut = optOut;
    this.updateAnalyticsPrivacyStatus();
    this.updateAppPrivacySettings();
  }

  /**
   * Takes a consent snapshot from OneTrust. From then on OneTrust replaces the
   * in-app toggle (device LAT still applies), and its opt out is mirrored into
   * storage for sessions where OneTrust fails to start. `isUserChoice` marks a
   * snapshot read as the user closed the preference center.
   */
  setOneTrustConsent(consent: OneTrustConsent, isUserChoice: boolean) {
    this.lazyInit();

    const settings = this.appPrivacySettings!;
    const wasDoNotSell = this.getDoNotSell();

    // First OneTrust snapshot, keep honouring an opt out from the legacy toggle
    if (!settings.oneTrustMigrated) {
      settings.oneTrustMigrated = true;
      settings.oneTrustCarriedOptOut = settings.optOut;
    }

    if (isUserChoice) settings.oneTrustCarriedOptOut = false;

    this.oneTrustConsent = consent;

    // Not collected isn't a choice, so it keeps the last known one
    if (consent.targeting !== -1) settings.optOut = this.getOneTrustOptOut();

    this.updateAppPrivacySettings();

    const isDoNotSell = this.getDoNotSell();
    AppData?.log.debug(
      `[Privacy][Privacy Service] setOneTrustConsent "doNotSell":${isDoNotSell}`,
      { ...consent, isUserChoice, carried: settings.oneTrustCarriedOptOut },
    );

    if (isDoNotSell !== wasDoNotSell) this.updateAnalyticsPrivacyStatus();
  }

  getDoNotSell(): boolean {
    this.lazyInit();

    const deviceDNS = this.deviceIntegration.getIsLat();
    if (this.oneTrustConsent) return deviceDNS || this.getOneTrustOptOut();

    const appDNS = !!this.appPrivacySettings?.optOut;
    return deviceDNS || appDNS;
  }

  // The DNS settings in the app, it's independent of the device opt-out setting
  getAppDoNotSell(): boolean {
    this.lazyInit();

    return !!this.appPrivacySettings!.optOut;
  }

  // GoogleIMA: Restricted Data Processing
  // https://developers.google.com/interactive-media-ads/docs/sdks/html5/dai/ccpa
  getRdpTag(): 0 | 1 {
    this.lazyInit();

    return this.getDoNotSell() ? rdpStatus.ON : rdpStatus.OFF_VIEWED;
  }

  // GoogleIMA: IAB Signal
  // https://developers.google.com/interactive-media-ads/docs/sdks/html5/dai/ccpa
  getIabUsPrivacy(): Ccpa {
    this.lazyInit();

    const usPrivacy = this.oneTrustConsent?.usPrivacy;
    if (usPrivacy && isCcpa(usPrivacy)) {
      // OneTrust's string knows nothing of device LAT or a carried opt out
      const isForcedOptOut =
        this.deviceIntegration.getIsLat() ||
        !!this.appPrivacySettings?.oneTrustCarriedOptOut;

      return isForcedOptOut
        ? (`${usPrivacy.slice(0, 2)}Y${usPrivacy.slice(3)}` as Ccpa)
        : usPrivacy;
    }

    return this.getDoNotSell() ? CcpaStatus.ON : CcpaStatus.OFF_VIEWED;
  }

  // GoogleIMA: IAB Global Privacy Platform, only available through OneTrust.
  // Passed through as is, device LAT and a carried opt out still reach ad
  // servers through the US Privacy string and the other opt out params.
  // https://developer.onetrust.com/onetrust/docs/iab-global-privacy-protocol-new
  getIabGpp(): IabGpp {
    const gppString = this.oneTrustConsent?.gppString;
    const gppSid = this.oneTrustConsent?.gppSid;

    // The string and its section ids are only meaningful as a pair
    if (!gppString || !gppSid || !GPP_SID_PATTERN.test(gppSid))
      return { gpp: '', gppSid: '' };

    // OneTrust separates section ids with `_`, ad servers expect `,`
    return { gpp: gppString, gppSid: gppSid.split('_').join(',') };
  }
}
